"use client";

import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { Skeleton } from "@/components/ui/skeleton";
import { Edit, Trash2, Eye, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { CustomerWithDetails } from "@/types";
import { userService } from "@/services/userService";
import { CustomerQueryParams, PaginationMeta } from "@/types/customer.api.types";
import { toast } from "sonner";

interface CustomerTableProps {
  onDelete: (member: CustomerWithDetails) => void;
  onView: (member: CustomerWithDetails) => void;
  onEdit: (member: CustomerWithDetails) => void;
  refreshTrigger?: number;
  filters: CustomerQueryParams;
  onFiltersChange: (filters: CustomerQueryParams) => void;
}

export function CustomersTable({
  onDelete,
  onView,
  onEdit,
  refreshTrigger,
  filters,
  onFiltersChange,
}: CustomerTableProps) {
  // State for customers and pagination
  const [customers, setCustomers] = useState<CustomerWithDetails[]>([]);
  const [pagination, setPagination] = useState<PaginationMeta>({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  });

  // UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch customers with pagination
  const fetchCustomers = useCallback(async (params: CustomerQueryParams) => {
    try {
      setLoading(true);
      setError(null);
      const response = await userService.getCustomersPaginated(params);
      setCustomers(response.data);
      setPagination(response.pagination);
    } catch (err: any) {
      const errorMessage =
        err?.message || "Failed to load customers. Please try again.";
      setError(errorMessage);
      console.error("Error fetching customers:", err);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch and when filters change
  useEffect(() => {
    fetchCustomers(filters);
  }, [filters, fetchCustomers]);

  // Refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger !== undefined && refreshTrigger > 0) {
      fetchCustomers(filters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  // Handle sort
  const handleSort = (sortBy: CustomerQueryParams["sortBy"]) => {
    onFiltersChange({
      ...filters,
      sortBy: sortBy || "createdAt",
      sortOrder:
        filters.sortBy === sortBy && filters.sortOrder === "asc" ? "desc" : "asc",
      page: 1,
    });
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= pagination.totalPages) {
      onFiltersChange({ ...filters, page });
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Handle items per page change
  const handleLimitChange = (limit: number) => {
    onFiltersChange({ ...filters, limit, page: 1 });
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get sort icon for a column
  const getSortIcon = (column: CustomerQueryParams["sortBy"]) => {
    if (filters.sortBy !== column) {
      return <ArrowUpDown className="ml-1 h-3 w-3" />;
    }
    return filters.sortOrder === "asc" ? (
      <ArrowUp className="ml-1 h-3 w-3" />
    ) : (
      <ArrowDown className="ml-1 h-3 w-3" />
    );
  };

  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    const maxVisiblePages = 5;

    if (pagination.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= pagination.totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (pagination.page <= 3) {
        for (let i = 2; i <= 4; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(pagination.totalPages);
      } else if (pagination.page >= pagination.totalPages - 2) {
        pages.push("ellipsis");
        for (let i = pagination.totalPages - 3; i <= pagination.totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push("ellipsis");
        for (let i = pagination.page - 1; i <= pagination.page + 1; i++) {
          pages.push(i);
        }
        pages.push("ellipsis");
        pages.push(pagination.totalPages);
      }
    }

    return pages;
  };

  // Calculate showing range
  const startIndex = (pagination.page - 1) * pagination.limit + 1;
  const endIndex = Math.min(
    pagination.page * pagination.limit,
    pagination.total
  );

  // Loading state
  if (loading && customers.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Customers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && customers.length === 0) {
    return (
      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Customers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <p className="text-destructive mb-4">{error}</p>
            <Button
              onClick={() => fetchCustomers(filters)}
              variant="outline"
            >
              Try Again
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-0 shadow-sm">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold">
            Customers {pagination.total > 0 && `(${pagination.total})`}
          </CardTitle>
          {pagination.total > 0 && (
            <div className="text-sm text-muted-foreground">
              Showing {startIndex} - {endIndex} of {pagination.total}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {/* Table */}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="border-b hover:bg-transparent">
                <TableHead className="text-xs font-medium w-10 text-muted-foreground">#</TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("name")}
                    className="h-8 px-2"
                  >
                    Name {getSortIcon("name")}
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("email")}
                    className="h-8 px-2"
                  >
                    Email {getSortIcon("email")}
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("phone")}
                    className="h-8 px-2"
                  >
                    Phone {getSortIcon("phone")}
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    disabled
                  >
                    Role
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    disabled
                  >
                    Type
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2"
                    disabled
                  >
                    Category
                  </Button>
                </TableHead>
                <TableHead className="text-xs font-medium">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSort("createdAt")}
                    className="h-8 px-2"
                  >
                    Created At {getSortIcon("createdAt")}
                  </Button>
                </TableHead>
                <TableHead className="text-right text-xs font-medium">
                  Actions
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && customers.length > 0 ? (
                [...Array(filters.limit || 10)].map((_, i) => (
                  <TableRow key={i} className="border-b">
                    <TableCell>
                      <Skeleton className="h-4 w-6" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-20" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  </TableRow>
                ))
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={9}
                    className="text-center text-muted-foreground py-8"
                  >
                    <div className="flex flex-col items-center gap-2">
                      <p>No customers found</p>
                    </div>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((member, index) => (
                  <TableRow key={member.id} className="border-b">
                    <TableCell className="text-sm text-muted-foreground w-10">
                      {startIndex + index}
                    </TableCell>
                    <TableCell className="font-medium text-sm">
                      {member.name}
                    </TableCell>
                    <TableCell className="text-sm">{member.email}</TableCell>
                    <TableCell className="text-sm">{member.phone}</TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className="capitalize text-xs bg-purple-50 text-purple-700 border-purple-100"
                      >
                        {(member.role ?? "").toLowerCase()}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {member.customerType ? (
                        <Badge
                          variant="outline"
                          className="capitalize text-xs bg-blue-50 text-blue-700 border-blue-100"
                        >
                          {member.customerType.replace(/-/g, " ")}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {member.customerCategory ? (
                        <Badge
                          variant="outline"
                          className="capitalize text-xs bg-emerald-50 text-emerald-700 border-emerald-100"
                        >
                          {member.customerCategory.replace(/-/g, " ")}
                        </Badge>
                      ) : (
                        <span className="text-sm text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatDate(member.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onView(member)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onEdit(member)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => onDelete(member)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="mt-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Items per page:</span>
              <Select
                value={String(filters.limit || 10)}
                onValueChange={(value) => handleLimitChange(Number(value))}
              >
                <SelectTrigger className="w-[100px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                  <SelectItem value="100">100</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (pagination.hasPrev) {
                        handlePageChange(pagination.page - 1);
                      }
                    }}
                    className={
                      !pagination.hasPrev
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
                {getPageNumbers().map((page, index) => {
                  if (page === "ellipsis") {
                    return (
                      <PaginationItem key={`ellipsis-${index}`}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    );
                  }
                  return (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          handlePageChange(page);
                        }}
                        isActive={pagination.page === page}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  );
                })}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (pagination.hasNext) {
                        handlePageChange(pagination.page + 1);
                      }
                    }}
                    className={
                      !pagination.hasNext
                        ? "pointer-events-none opacity-50"
                        : "cursor-pointer"
                    }
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
